import { EventListeners, MinzeElement, Reactive } from "minze";
import { Locale } from "../../modules/locale";
import { EMAIL_REGEX } from "../../modules/utils";
import { submitForm } from "../../constants";
import { SquidexTextLabels } from "../../modules/squidex/labels";

Locale.getInstance().registerKeySet("fr", "kitForm", {
  nameLabel: "Prénom",
  surnameLabel: "Nom",
  emailLabel: "Adresse courriel",
  regionLabel: "Région",
  schoolLabel: "Nom de l’école ou du centre scolaire",
  userTypeLabel: "Fonction",
  userTypeAnnotationLabel: "Précision de la fonction",
  send: "Soumettre le formulaire",
  requiredError: "Requis",
  invalidEmailError: "Le courriel est invalide",
  submittedMessage:
    "Formulaire soumis! Vous recevrez un courriel de confirmation sous peu.",
  checkboxLabel:
    "J’accepte de recevoir l’infolettre Tel-jeunes destinée au personnel scolaire pour rester informé.e en tout temps des nouveaux ajouts à la trousse Tj et de toute autre nouvelle pertinente.",
  userTypeLabels:
    "Enseignant.e,Conseiller.ère pédagogique,Intervenant.e scolaire,Membre de la direction,Autre (précisez)",
});

Locale.getInstance().registerKeySet("en", "kitForm", {
  nameLabel: "First Name",
  surnameLabel: "Last Name",
  emailLabel: "Email",
  regionLabel: "Region",
  schoolLabel: "Name of School or Academic Services Centre",
  userTypeLabel: "Role",
  userTypeAnnotationLabel: "Specify your Role",
  send: "Submit",
  requiredError: "Required",
  invalidEmailError: "Email is invalid",
  submittedMessage:
    "Your form was submitted! You will receive a confirmation email shortly",
  checkboxLabel:
    "I agree to receive Tel-jeunes’ newsletter for educators so that I can stay informed about new additions to the Tj ToolKit, and other relevant updates.",
  userTypeLabels:
    "Teacher, Academic Advisor, School Counsellor, Member of the Administration, Other (please specify)",
});

export interface TjKitForm {
  errors: { [fieldName: string]: string };
  values: { [fieldName: string]: any };
  submitted: boolean;
  otherUserType: boolean;
}

const regionsLabels =
  "Abitibi-Témiscamingue,Bas St-Laurent,Centre-du-Québec,Chaudière-Appalaches,Côte-Nord,Estrie,Gaspésie–Îles-de-la-Madeleine,Lanaudière,Laval,Laurentides,Mauricie,Montréal,Montérégie,Nord-du-Quebec,Outaouais,Québec,Saguenay-Lac-Saint-Jean";
const regionsOptions = [
  "abitibi-témiscamingue",
  "bas-st-laurent",
  "centre-du-québec",
  "chaudière-appalaches",
  "côte-nord",
  "estrie",
  "gaspésie–îles-de-la-madeleine",
  "lanaudière",
  "laval",
  "laurentides",
  "mauricie",
  "montréal",
  "montérégie",
  "nord-du-quebec",
  "outaouais",
  "québec",
  "saguenay-lac-saint-jean",
];

const userTypeOptions = [
  "enseignant",
  "сonseille",
  "intervenant",
  "membre-de-la-direction",
  "autre",
];

export class TjKitForm extends MinzeElement {
  reactive: Reactive = [
    ["errors", {}],
    ["values", {}],
    ["submitted", false],
    ["otherUserType", false],
  ];
  isMobile = window.matchMedia("(max-width: 768px)").matches;

  setValues = () => {
    const nameField = this.select(`#name`)?.getAttribute("value");
    const surnameField = this.select(`#surname`)?.getAttribute("value");
    const emailField = this.select(`#email`)?.getAttribute("value");
    const regionField = this.select(`#region`)?.getAttribute("value");
    const schoolField = this.select(`#school`)?.getAttribute("value");
    const userTypeField = this.select(`#userType`)?.getAttribute("value");
    const userTypeAnnotationField =
      this.select(`#userTypeAnnotation`)?.getAttribute("value");
    const confirmField = this.select(`#confirm`)?.getAttribute("checked");
    this.values = {
      name: nameField,
      surname: surnameField,
      email: emailField,
      region: regionField,
      school: schoolField,
      userType: userTypeField,
      userTypeAnnotation: userTypeAnnotationField,
      confirm: confirmField,
    };
  };

  submitForm = async () => {
    this.submitted = false;
    this.errors = {};
    this.setValues();
    if (!this.values["name"])
      this.errors = {
        ...this.errors,
        name: SquidexTextLabels.getKey("kitForm.requiredError"),
      };
    if (!this.values["surname"])
      this.errors = {
        ...this.errors,
        surname: SquidexTextLabels.getKey("kitForm.requiredError"),
      };
    if (!this.values["email"])
      this.errors = {
        ...this.errors,
        email: SquidexTextLabels.getKey("kitForm.requiredError"),
      };
    else if (!EMAIL_REGEX.test(this.values["email"])) {
      this.errors = {
        ...this.errors,
        email: SquidexTextLabels.getKey("kitForm.invalidEmailError"),
      };
    }
    if (!this.values["region"])
      this.errors = {
        ...this.errors,
        region: SquidexTextLabels.getKey("kitForm.requiredError"),
      };
    if (!this.values["school"])
      this.errors = {
        ...this.errors,
        school: SquidexTextLabels.getKey("kitForm.requiredError"),
      };
    if (!this.values["userType"])
      this.errors = {
        ...this.errors,
        userType: SquidexTextLabels.getKey("kitForm.requiredError"),
      };
    if (
      this.values["userType"] === "autre" &&
      !this.values["userTypeAnnotation"]
    ) {
      this.errors = {
        ...this.errors,
        userTypeAnnotation: SquidexTextLabels.getKey("kitForm.requiredError"),
      };
    }
    if (!Object.values(this.errors).length) {
      const response = await submitForm("354wypi", {
        ...this.values,
        confirm: this.values["confirm"] === "true" ? "TRUE" : "FALSE",
      });
      if (response.status === "success") {
        this.submitted = true;
        this.values = {};
        this.rerender(true);
      }
    } else {
      location.href = `#${Object.keys(this.errors)[0]}`;
      this.rerender();
    }
  };

  onUserTypeChanged = () => {
    const detail = (event as CustomEvent<{ value: string }>).detail;
    this.setValues();
    this.otherUserType = detail.value === "autre";
  };

  html = () => `
    <tj-box mt="48px" mb="48px">
      <tj-separator-icon></tj-separator-icon>
    </tj-box>
    <tj-box mb="16px" display="flex" flexDirection="${this.isMobile ? "column" : "row"}" rowGap="16px" columnGap="16px">
      <tj-input
        id="name"
        name="name"
        placeholder="${SquidexTextLabels.getKey("kitForm.nameLabel")}"
        error="${this.errors["name"]}"
        value="${this.values["name"]}"
      ></tj-input>
      <tj-input
        id="surname"
        name="surname"
        placeholder="${SquidexTextLabels.getKey("kitForm.surnameLabel")}"
        error="${this.errors["surname"]}"
        value="${this.values["surname"]}"
      ></tj-input>
    </tj-box>
    <tj-box mb="16px">
      <tj-input
        id="email"
        name="email"
        type="email"
        placeholder="${SquidexTextLabels.getKey("kitForm.emailLabel")}"
        error="${this.errors["email"]}"
        value="${this.values["email"]}"
      ></tj-input>
    </tj-box>
    <tj-box mb="16px">
      <tj-select
        id="region"
        name="region"
        placeholder="${SquidexTextLabels.getKey("kitForm.regionLabel")}"
        default-value=""
        selectOptions="${regionsOptions.join(",")}"
        selectOptionLabels="${regionsLabels}"
        error="${this.errors["region"]}"
        value="${this.values["region"]}"
      ></tj-select>
    </tj-box>
    <tj-box mb="16px">
      <tj-input
        id="school"
        name="school"
        placeholder="${SquidexTextLabels.getKey("kitForm.schoolLabel")}"
        error="${this.errors["school"]}"
        value="${this.values["school"]}"
      ></tj-input>
    </tj-box>
    <tj-box mb="16px" display="flex" columnGap="16px">
      <tj-select
        id="userType"
        name="userType"
        placeholder="${SquidexTextLabels.getKey("kitForm.userTypeLabel")}"
        default-value="${this.otherUserType ? "autre" : this.values["userType"] || ""}"
        selectOptions="${userTypeOptions.join(",")}"
        selectOptionLabels="${SquidexTextLabels.getKey("kitForm.userTypeLabels")}"
        error="${this.errors["userType"]}"
        value="${this.values["userType"]}"
      ></tj-select>
      ${
        this.otherUserType
          ? `
        <tj-input
          id="userTypeAnnotation"
          name="userTypeAnnotation"
          placeholder="${SquidexTextLabels.getKey("kitForm.userTypeAnnotationLabel")}"
          error="${this.errors["userTypeAnnotation"]}"
          value="${this.values["userTypeAnnotation"]}"
        ></tj-input>
      `
          : ""
      }
    </tj-box>
    <tj-box mb="16px">
      <tj-checkbox
        id="confirm"
        name="confirm"
        default-value="${this.confirmPolicy}"
        checked="${this.confirmPolicy}"
        label="${SquidexTextLabels.getKey("kitForm.checkboxLabel")}"
      />
    </tj-box>
    <tj-box mt="48px" mb="48px">
      <tj-separator-icon></tj-separator-icon>
    </tj-box>
    <tj-box mb="16px" display="flex" alignItems="center" justifyContent="center" flexDirection="column">
      <tj-button id="submit" name="save" width="fit-content"
      class="kit-form-submit-button"
      >
        ${SquidexTextLabels.getKey("kitForm.send")}
      </tj-button>
      ${
        this.submitted
          ? `
        <tj-box mt="16px" mb="16px">
          <tj-text>${SquidexTextLabels.getKey("kitForm.submittedMessage")}</tj-text>
        </tj-box>
      `
          : ""
      }
    </tj-box>
  `;

  css = () => `
    :host {
      overflow-x: hidden;
    }
    .title {
      font-weight: 500;
      font-size: 40px;
      line-height: 120%;
      color: #1E007D;
      margin: 80px 0 8px 0;
    }
    tj-separator-icon {
      overflow: hidden;
    }
  `;
  eventListeners: EventListeners = [
    ["#submit", "click", this.submitForm.bind(this)],
    [
      window,
      "tj:select-value-change-userType",
      this.onUserTypeChanged.bind(this),
    ],
  ];
}
